/**
 * MidfielderFormationBot - Midfielder for IMPOSSIBLE mode.
 * Extends FormationBot with support, receive, pass, and advance behaviors.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { FormationBot } from "./FormationBot";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";
import { KickType } from "./HumanoidMesh";

// Midfielder-specific constants
const PASS_POWER = 10;
const PASS_COOLDOWN = 1.0;
const ADVANCE_SPEED = 5.5;

/**
 * Formation midfielder bot for IMPOSSIBLE mode
 * - Zone: middle third
 * - States: SUPPORT, RECEIVE, PASS, ADVANCE, SHOOT, FORMATION_RETURN
 * - Behavior: Control midfield, create passing triangles
 */
export class MidfielderFormationBot extends FormationBot {
  private _passTimer = 0;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    role: AIRole,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      role,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      `midfielderFormation_${role}`
    );
    
    // Set stretch for midfielders
    this.setStretch(12, 10);
  }

  /**
   * Yellow jersey color for formation midfielders
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.9, 0.8, 0.1); // Yellow jersey
  }

  /**
   * Update with pass timer
   */
  update(dt: number, teammates: BotBase[]): void {
    if (this._passTimer > 0) this._passTimer -= dt;
    super.update(dt, teammates);
  }

  /**
   * Midfielder-specific decision making
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Check if ball is in our zone
    const ballInOurZone = ballPos.z >= this._allowedZone.minZ && ballPos.z <= this._allowedZone.maxZ;

    // If we have possession and are in shooting range
    if (this.hasBallPossession && distToGoal < 20 && Math.abs(myPos.x) < 6) {
      this._state = AIState.SHOOT;
      return;
    }

    // If we have possession, look to advance or pass
    if (this.hasBallPossession && this._passTimer <= 0) {
      this._state = AIState.ADVANCE;
      return;
    }

    // If we have possession but can't pass yet, dribble
    if (this.hasBallPossession) {
      this._state = AIState.DRIBBLE;
      return;
    }

    // Tackle if player has ball and we're close
    if (this._player && this._player.hasBallPossession && distToBall < 2.5 && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // If ball is in our zone, chase it
    if (ballInOurZone) {
      this._state = AIState.CHASE_BALL;
      return;
    }

    // If ball is behind us, support the defense
    if (ballPos.z > this._allowedZone.maxZ) {
      this._state = AIState.SUPPORT;
      return;
    }

    // If ball is ahead of us, move to receive
    if (ballPos.z < this._allowedZone.minZ) {
      this._state = AIState.RECEIVE;
      return;
    }

    // Check if should return to formation
    if (this._shouldReturnToFormation(ballPos, myPos)) {
      this._state = AIState.FORMATION_RETURN;
      return;
    }

    // Default: hold position
    this._state = AIState.HOLD_POSITION;
  }

  /**
   * Handle state behavior with midfielder-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.SUPPORT:
        this._handleSupport(ballPos, myPos, dt);
        break;
      case AIState.RECEIVE:
        this._handleReceive(ballPos, myPos, distToBall, dt);
        break;
      case AIState.ADVANCE:
        this._handleAdvance(ballPos, myPos, distToGoal, dt);
        break;
      case AIState.PASS:
        this._handlePass(ballPos, myPos);
        break;
      default:
        super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
    }
  }

  /**
   * Support play - position for pass options
   */
  private _handleSupport(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    // Get dynamic position based on ball
    let targetPos = this._getDynamicPosition(ballPos);
    
    // Position near edge of our zone toward the ball
    targetPos.z = Math.max(targetPos.z, this._allowedZone.maxZ - 3);
    
    // Create space horizontally
    if (Math.abs(ballPos.x - myPos.x) < 4) {
      targetPos.x = myPos.x + (myPos.x > 0 ? 5 : -5);
    }
    
    // Clamp to zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);
    
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = 5.5 * 0.7;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }
    
    // Face the ball
    const toBall = ballPos.subtract(myPos);
    const angle = Math.atan2(toBall.x, toBall.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
  }

  /**
   * Receive ball behavior
   */
  private _handleReceive(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToBall: number, dt: number): void {
    const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
    let targetPos: BABYLON.Vector3;

    if (ballVel && ballVel.length() > 2) {
      // Predict ball position
      const ballDir = new BABYLON.Vector3(ballVel.x, 0, ballVel.z).normalize();
      const timeToIntercept = distToBall / Math.max(ballVel.length(), 1);
      targetPos = ballPos.add(ballDir.scale(timeToIntercept * 2));
    } else {
      // Move to edge of zone toward ball
      targetPos = new BABYLON.Vector3(ballPos.x * 0.3, 0, this._allowedZone.minZ + 3);
    }

    // Clamp to zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);

    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.5) {
      const speed = dist > 5 ? 7.5 : 5.5;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }

    const angle = Math.atan2(dx, dz);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);

    // If close to ball, gain possession
    if (distToBall < 2.0) {
      this.hasBallPossession = true;
      this._state = AIState.ADVANCE;
    }
  }

  /**
   * Advance with ball
   */
  private _handleAdvance(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToGoal: number, dt: number): void {
    const goalPos = new BABYLON.Vector3(0, 0, -30);
    const toGoal = goalPos.subtract(myPos).normalize();

    // Dribble toward goal
    if (this._kickTimer <= 0) {
      const dribbleDir = new BABYLON.Vector3(toGoal.x * 0.5, 0.1, toGoal.z * 0.5).normalize();
      this._ball.kick(dribbleDir, 3.5);
      this._kickTimer = 0.35;
      this.animator.triggerKick(KickType.Normal);
    }

    // Move toward ball
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.3) {
      myPos.x += (dx / dist) * ADVANCE_SPEED * dt;
      myPos.z += (dz / dist) * ADVANCE_SPEED * dt;
    }

    this._currentSpeed = ADVANCE_SPEED;

    const angle = Math.atan2(toGoal.x, toGoal.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);

    // If in shooting range or near edge of zone, look to pass or shoot
    if (distToGoal < 20 && Math.abs(myPos.x) < 6) {
      this._state = AIState.SHOOT;
    } else if (myPos.z < this._allowedZone.minZ + 5) {
      this._state = AIState.PASS;
    }
  }

  /**
   * Pass to forward
   */
  private _handlePass(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): void {
    if (this._kickTimer > 0) return;
    
    // Pass forward to striker/winger area
    const passTarget = new BABYLON.Vector3(
      myPos.x * 0.3,
      0,
      -15 // Forward area
    );
    
    const dx = passTarget.x - myPos.x;
    const dz = passTarget.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.1) {
      const passDir = new BABYLON.Vector3(dx / dist, 0.1, dz / dist).normalize();
      this._ball.kick(passDir, PASS_POWER);
      
      this._kickTimer = PASS_COOLDOWN;
      this._passTimer = PASS_COOLDOWN;
      this.animator.triggerKick(KickType.Normal);
      this.hasBallPossession = false;
      this._state = AIState.SUPPORT;
    }
  }

  /**
   * Reset pass timer
   */
  resetPosition(): void {
    super.resetPosition();
    this._passTimer = 0;
  }
}